import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import type { Session } from "@supabase/supabase-js";
import type { Profile, UserRole, UserAssignment } from "@/types/database";
import { Sentry } from "@/lib/sentry";
import { identifyUser, resetAnalyticsUser, trackEvent } from "@/lib/analytics";

interface User {
  id: string;
  email: string;
  fullName: string;
  role: UserRole;
  avatarUrl?: string;
  /** All org IDs this user is assigned to (via user_assignments) */
  assignedOrgIds: string[];
  /** Primary org ID — first strata_admin/PM assignment, or first host assignment */
  primaryOrgId?: string;
}

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  assignments: UserAssignment[];
  session: Session | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Currently selected org for PM multi-building context */
  activeOrgId: string | null;
  setActiveOrgId: (orgId: string) => void;
  /** Role currently being impersonated by system_admin (null = no impersonation) */
  devRole: UserRole | null;
  /** Set impersonated role — only works for system_admin users */
  setDevRole: (role: UserRole | null) => void;
  /** Effective role: devRole when impersonating, otherwise user.role */
  effectiveRole: UserRole;
  login: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string, fullName: string, role: UserRole, emailRedirectTo?: string) => Promise<{ error: string | null }>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: string | null }>;
}

const AuthContext = createContext<AuthContextType | null>(null);

function mapProfile(profile: Profile, assignments: UserAssignment[]): User {
  const orgIds = assignments.map((a) => a.org_id);
  // Primary org: first admin/PM assignment, then first host assignment
  const adminAssignment = assignments.find((a) => a.role === "strata_admin" || a.role === "property_manager");
  const primaryOrgId = adminAssignment?.org_id ?? assignments[0]?.org_id ?? undefined;

  return {
    id: profile.id,
    email: profile.email,
    fullName: profile.full_name,
    role: profile.role as UserRole,
    avatarUrl: profile.avatar_url ?? undefined,
    assignedOrgIds: orgIds,
    primaryOrgId,
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [assignments, setAssignments] = useState<UserAssignment[]>([]);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeOrgId, setActiveOrgIdState] = useState<string | null>(null);
  const [devRole, setDevRoleState] = useState<UserRole | null>(() => {
    try { return (sessionStorage.getItem("devRole") as UserRole | null) ?? null; } catch { return null; }
  });

  const setActiveOrgId = (orgId: string) => {
    // Membership check is the single source of truth for "can this user act
    // as this org" — every permission check downstream (usePmPermissions,
    // RLS) keys off activeOrgId, so we refuse to set it to an org the user
    // isn't actually assigned to. system_admin is exempt (can impersonate
    // any org by design). This guards against a caller passing an arbitrary
    // orgId (e.g. from unvalidated input) rather than one drawn from the
    // user's own assignments.
    const isSysAdmin = user?.role === "system_admin";
    if (!isSysAdmin && !user?.assignedOrgIds.includes(orgId)) {
      console.warn(`[AuthContext] Refused to set activeOrgId to "${orgId}" — user is not assigned to this org`);
      return;
    }
    setActiveOrgIdState(orgId);
    // Persist selection to sessionStorage so it survives page refresh
    sessionStorage.setItem("activeOrgId", orgId);
  };

  const setDevRole = (role: UserRole | null) => {
    setDevRoleState(role);
    try {
      if (role) { sessionStorage.setItem("devRole", role); }
      else { sessionStorage.removeItem("devRole"); }
    } catch { /* sessionStorage unavailable */ }
  };

  // Build a fallback user from Supabase auth metadata when profiles table is unavailable
  const buildFallbackUser = (authUser: any): User => ({
    id: authUser.id,
    email: authUser.email ?? "",
    fullName: authUser.user_metadata?.full_name ?? authUser.email?.split("@")[0] ?? "User",
    role: (authUser.user_metadata?.role as UserRole) ?? "strata_admin",
    avatarUrl: undefined,
    assignedOrgIds: [],
    primaryOrgId: undefined,
  });

  // Fetch profile + assignments from DB, with fallback to auth metadata
  const fetchAndSetUser = async (authUser: any) => {
    try {
      // Fetch profile
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", authUser.id)
        .single();

      if (profileError || !profileData) {
        throw new Error("Profile not found");
      }

      const p = profileData as Profile;

      // Fetch user assignments (with org details)
      const { data: assignmentData, error: assignmentError } = await supabase
        .from("user_assignments")
        .select("*, organization:organizations(id, name, address, total_units, plan, admin_id, created_at, updated_at)")
        .eq("user_id", authUser.id);

      if (assignmentError) {
        console.warn("user_assignments unavailable:", assignmentError.message);
      }

      const userAssignments: UserAssignment[] = (assignmentData ?? []) as UserAssignment[];

      setProfile(p);
      setAssignments(userAssignments);
      const mappedUser = mapProfile(p, userAssignments);
      setUser(mappedUser);

      // Set Sentry user context (id + role only — no PII like email or name)
      Sentry.setUser({ id: mappedUser.id, role: mappedUser.role });

      // Identify user in PostHog analytics (role + org, no PII)
      identifyUser({
        userId:       mappedUser.id,
        role:         mappedUser.role,
        primaryOrgId: mappedUser.primaryOrgId,
      });

      // Update last_seen_at (fire-and-forget — never blocks auth)
      supabase
        .from("profiles")
        .update({ last_seen_at: new Date().toISOString() })
        .eq("id", mappedUser.id)
        .then(({ error }) => {
          if (error) console.warn("[AuthContext] last_seen_at update failed:", error.message);
        });

      // Set active org from session storage or default to primary.
      // system_admin can freely switch to any org (no assignment required).
      const storedOrgId = sessionStorage.getItem("activeOrgId");
      const isSysAdmin = p.role === "system_admin";
      const validStoredOrg = storedOrgId && (isSysAdmin || mappedUser.assignedOrgIds.includes(storedOrgId));
      setActiveOrgIdState(validStoredOrg ? storedOrgId : (mappedUser.primaryOrgId ?? null));

      return;
    } catch (e) {
      // Table may not exist yet or profile not created
      console.warn("Profiles/assignments unavailable — using auth metadata as fallback");
    }

    // Fallback: use auth metadata so the user can still navigate
    const fallback = buildFallbackUser(authUser);
    setProfile(null);
    setAssignments([]);
    setUser(fallback);
    setActiveOrgIdState(null);
    Sentry.setUser({ id: fallback.id, role: fallback.role });
  };

  // Initialize auth state
  useEffect(() => {
    // Race getSession() against a 5-second timeout so the loading spinner
    // never hangs indefinitely when Supabase is unreachable or credentials
    // are invalid. If the timeout wins we treat it as "no session" and let
    // the router redirect to /login.
    const SESSION_TIMEOUT_MS = 5000;
    const sessionPromise = supabase.auth.getSession();
    const timeoutPromise = new Promise<{ data: { session: null } }>((resolve) =>
      setTimeout(() => {
        console.warn("[AuthContext] getSession() timed out — treating as unauthenticated");
        resolve({ data: { session: null } });
      }, SESSION_TIMEOUT_MS)
    );

    Promise.race([sessionPromise, timeoutPromise]).then(async ({ data: { session: s } }) => {
      setSession(s);
      if (s?.user) {
        await fetchAndSetUser(s.user);
      }
      setIsLoading(false);
    });

    // IMPORTANT: the callback must be synchronous — making it async causes the
    // Supabase JS client to deadlock when supabase.auth.signOut() is called
    // while a previous async invocation (with in-flight DB queries) is still
    // running. All async work is deferred to the next event-loop tick via
    // setTimeout so the auth state machine can complete its own bookkeeping
    // before we re-enter it with further Supabase calls.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, s) => {
        setSession(s);
        // Defer all async work to next tick — prevents Supabase client deadlock
        setTimeout(async () => {
          if (s?.user) {
            if (event === "SIGNED_IN") {
              // Small delay to allow trigger to create profile on signup
              await new Promise((r) => setTimeout(r, 500));
              trackEvent("user_signed_in");
            }
            await fetchAndSetUser(s.user);
          } else {
            setProfile(null);
            setAssignments([]);
            setUser(null);
            setActiveOrgIdState(null);
            setDevRoleState(null);
            try {
              sessionStorage.removeItem("activeOrgId");
              sessionStorage.removeItem("devRole");
            } catch { /* sessionStorage unavailable */ }
            Sentry.setUser(null); // clear user context on sign-out
            trackEvent("user_signed_out");
            resetAnalyticsUser();  // clear PostHog identity
          }
          setIsLoading(false);
        }, 0);
      }
    );

    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };
    return { error: null };
  };

  const signUp = async (
    email: string,
    password: string,
    fullName: string,
    role: UserRole,
    emailRedirectTo?: string,
  ) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName, role },
        ...(emailRedirectTo ? { emailRedirectTo } : {}),
      },
    });
    if (error) return { error: error.message };
    return { error: null };
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setProfile(null);
    setAssignments([]);
    setUser(null);
    setActiveOrgIdState(null);
    setDevRoleState(null);
    try {
      sessionStorage.removeItem("activeOrgId");
      sessionStorage.removeItem("devRole");
    } catch { /* sessionStorage unavailable */ }
    Sentry.setUser(null); // clear user context on explicit logout
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) return { error: error.message };
    return { error: null };
  };

  // effectiveRole: what role should be used for UX/routing decisions.
  // system_admin can impersonate another role via devRole.
  const effectiveRole: UserRole =
    (user?.role === "system_admin" && devRole) ? devRole : (user?.role ?? "strata_admin");

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        assignments,
        session,
        isAuthenticated: !!session,
        isLoading,
        activeOrgId,
        setActiveOrgId,
        devRole,
        setDevRole,
        effectiveRole,
        login,
        signUp,
        logout,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export type { UserRole, User };
